import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowLeft } from 'lucide-react';
import ProblemCategoryCard from '../Landing/components/ProblemCategoryCard.tsx';
import SurrogateCard from '../Landing/components/SurrogateCard.tsx';
import { executives } from '../Landing/data/executives.ts';
import { proposals } from '../Landing/data/mockData.ts';
import { motion } from 'framer-motion';
import Logo from '../../components/common/Logo.tsx';
import SubmitProposalButton from '../../components/common/SubmitProposalButton.tsx';
import ProposalCard from '../../components/common/ProposalCard.tsx';

const Container = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  padding: theme.spacing(4),
  paddingTop: theme.spacing(12), // Add padding for the header
  background: 'rgba(26, 26, 46, 0.1)',
}));

const Header = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  zIndex: 100,
  background: 'rgba(26, 26, 46, 0.8)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid rgba(138, 43, 226, 0.2)',
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
}));

const ContentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  gap: theme.spacing(2),
}));

const Section = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
}));

const ExecutivePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const executive = executives.find(exec => exec.id === id);

  const handleViewProposal = (id: string) => {
    navigate(`/proposal-detail/${id}`);
  };

  if (!executive) {
    return (
      <>
        <Header>
          <Logo size="medium" />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SubmitProposalButton text="Create Proposal" />
            <IconButton 
              onClick={() => navigate('/')}
              sx={{ 
                color: '#8a2be2',
                '&:hover': { 
                  background: 'rgba(138, 43, 226, 0.1)' 
                }
              }}
            >
              <ArrowLeft />
            </IconButton>
          </Box>
        </Header>
        <Container>
          <Typography variant="h4" color="error">Executive not found</Typography>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header>
        <Logo size="medium" />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SubmitProposalButton text="Create Proposal" />
          <IconButton 
            onClick={() => navigate('/')}
            sx={{ 
              color: '#8a2be2',
              '&:hover': { 
                background: 'rgba(138, 43, 226, 0.1)' 
              }
            }}
          >
            <ArrowLeft />
          </IconButton>
        </Box>
      </Header>
      <Container>
        <ContentHeader>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#fff',
                textShadow: '0 0 10px rgba(138, 43, 226, 0.5)',
              }}
            >
              {executive.title} Dashboard
            </Typography>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                mt: 1 
              }}
            >
              Manage your executive responsibilities and resources
            </Typography>
          </Box>
        </ContentHeader>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Section>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#fff',
                mb: 3,
                textShadow: '0 0 10px rgba(138, 43, 226, 0.3)',
              }}
            >
              Related Proposals
            </Typography>
            <Grid container spacing={3}>
              {proposals.slice(0, 3).map((proposal) => (
                <Grid item xs={12} md={4} key={proposal.id}>
                  <ProposalCard 
                    proposal={proposal} 
                    onViewDetails={handleViewProposal} 
                  />
                </Grid>
              ))}
            </Grid>
          </Section>

          <Section>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#fff',
                mb: 3,
                textShadow: '0 0 10px rgba(138, 43, 226, 0.3)',
              }}
            >
              Available Surrogates
            </Typography>
            <Grid container spacing={3}>
              {executive.surrogates.map((surrogate) => (
                <Grid item xs={12} sm={6} key={surrogate.id}>
                  <SurrogateCard surrogate={surrogate} />
                </Grid>
              ))}
            </Grid>
          </Section>

          <Section>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#fff',
                mb: 3,
                textShadow: '0 0 10px rgba(138, 43, 226, 0.3)',
              }}
            >
              Problem Categories
            </Typography>
            <Grid container spacing={3}>
              {executive.problemCategories.map((category, index) => (
                <Grid item xs={12} sm={6} md={4} key={category.id}>
                  <ProblemCategoryCard category={category} index={index} />
                </Grid>
              ))}
            </Grid>
          </Section>
        </motion.div>
      </Container>
    </>
  );
};

export default ExecutivePage;
//export default ExecutiveDashboard;